import { Flex } from '@servicetitan/anvil2';
import {
    usePortalStore,
    usePortalMode,
    formatAddress,
    AppointmentWindow,
    getJobNameDisplay,
} from '@servicetitan/cp-ui';
import { DateTimeUtils } from '@servicetitan/datetime-utils';
import {
    Button,
    Card,
    Stack,
    Headline,
    BodyText,
    Icon,
    BodyTextProps,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, SyntheticEvent, useCallback } from 'react';
import { getDateText } from '../../../../../utils/date-helper';
import { AppSettingsStore } from '../../../../common/stores/app-settings.store';
import { JobListItemStatus, JobListItemWeb } from '../../../api/portal-dashboard.api';
import { SettingsStore } from '../../../api/settings.store';
import { getAppointmentState } from '../../../utils/helpers';
import * as Styles from '../list-item.module.less';
import { AppointmentDrawerStore } from './appointment-drawer/stores/appointment-drawer.store';
import { JobStatusTag } from './job-status-tag';
import { JobsUrlMap } from './jobs-url-map';

interface ScheduledJobsListItemProps {
    job: JobListItemWeb;
}

export const ScheduledJobsListItem: FC<ScheduledJobsListItemProps> = observer(props => {
    const { job } = props;
    const mode = usePortalMode();
    const { event } = usePortalStore();
    const [drawerStore] = useDependencies(AppointmentDrawerStore);
    const [{ appointmentTimeOptions }] = useDependencies(SettingsStore);
    const [{ serviceAgreementsEnabled }] = useDependencies(AppSettingsStore);
    const jobNumber = Number(job?.number ?? 0);
    const jobHref = `${JobsUrlMap.list}/${jobNumber}`;

    const [dateTimeUtils] = useDependencies(DateTimeUtils);
    const [settingsStore] = useDependencies(SettingsStore);
    const jobNameDisplay =
        getJobNameDisplay(
            job?.type,
            job?.serviceLocation?.address
                ? formatAddress(job?.serviceLocation?.address)
                : undefined,
            job?.serviceLocation?.name,
            job?.status === JobListItemStatus.Completed
                ? getDateText(dateTimeUtils, job?.lastAppointmentStartDate)
                : getDateText(dateTimeUtils, job?.firstNotCompletedAppointmentStartDate),
            settingsStore.jobNameDisplayOptions
        ) ?? '';

    const onViewAllJobsClick = (e: SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        event('redirect', ['job', { itemId: jobNumber }]);
    };

    const viewJobAppointments = useCallback(() => {
        if (drawerStore.open) {
            drawerStore.open(job);
        }
    }, [drawerStore, job]);

    const viewJobButton = (
        <Button fill="subtle" primary size="small" href={jobHref} onClick={onViewAllJobsClick}>
            View Job
        </Button>
    );

    return (
        <Card
            padding="thin"
            className={classNames(Styles.listItem, { [Styles.listItemMobile]: mode.mobile })}
        >
            <Card.Section>
                <Flex columnGap="2" rowGap="0" justifyContent="space-between" alignItems="center">
                    <Flex columnGap="2" rowGap="2" direction="column" className="min-w-0">
                        <Flex alignItems="center" columnGap="2" rowGap="0">
                            <Headline size="medium" className="m-b-0 t-truncate">
                                {'Job #' + job.number + ': ' + jobNameDisplay}
                            </Headline>
                            {!mode.mobile && (
                                <Flex>
                                    <JobStatusTag status={job.status} />
                                </Flex>
                            )}
                        </Flex>
                        <Flex
                            rowGap="0"
                            columnGap="3"
                            alignItems="baseline"
                            direction={mode.mobile ? 'column' : 'row'}
                            wrap={mode.desktop ? 'nowrap' : 'wrap'}
                            className="m-b--1"
                        >
                            <BodyText subdued size="small" className="m-b-1">
                                <Icon
                                    name="event_available"
                                    size={16}
                                    className="m-r-half m-b-half"
                                />
                                {`${job.appointmentCount} ${job.appointmentCount === 1 ? 'Appointment' : 'Appointments'}`}
                                <Button
                                    outline
                                    className="m-l-1"
                                    size="xsmall"
                                    onClick={viewJobAppointments}
                                >
                                    View
                                </Button>
                            </BodyText>
                            {appointmentTimeOptions && (
                                <AppointmentWindow
                                    appointmentState={getAppointmentState(job.status)}
                                    appointmentTimeOptions={appointmentTimeOptions}
                                    startDate={job.firstNotCompletedAppointmentStartDate}
                                    endDate={job.firstNotCompletedAppointmentEndDate}
                                    arrivalWindowPlanStartDate={job.arrivalWindowPlanStartDate}
                                    arrivalWindowPlanEndDate={job.arrivalWindowPlanEndDate}
                                    size="small"
                                    subdued
                                    className="m-b-1"
                                    upcoming={job.status === JobListItemStatus.Scheduled}
                                />
                            )}
                            {job.serviceLocation?.address && (
                                <LocationBodyText className="m-b-1">
                                    {formatAddress(job.serviceLocation.address)}
                                </LocationBodyText>
                            )}
                            {job.serviceAgreementName && serviceAgreementsEnabled && (
                                <BodyText subdued size="small" className="m-b-1">
                                    <Icon
                                        name="service_agreements"
                                        size={16}
                                        className="m-r-half m-b-half"
                                    />
                                    {job.serviceAgreementName}
                                </BodyText>
                            )}
                        </Flex>
                    </Flex>
                    {!mode.mobile && <Stack.Item shrink={0}>{viewJobButton}</Stack.Item>}
                </Flex>
            </Card.Section>
            {mode.mobile && (
                <Card.Section>
                    <Flex className="m-y--1" justifyContent="space-between" alignItems="center">
                        <Flex shrink={0} className="m-r-1">
                            <JobStatusTag status={job.status} />
                        </Flex>
                        {viewJobButton}
                    </Flex>
                </Card.Section>
            )}
        </Card>
    );
});

export const LocationBodyText: FC<BodyTextProps> = observer(props => {
    const { children, className, location, ...otherProps } = props;
    return (
        <BodyText
            subdued
            size="small"
            className={classNames('t-truncate', className)}
            {...otherProps}
        >
            <Icon name="place" size={16} className="m-r-half m-b-half" />
            {children}
        </BodyText>
    );
});
